import { Inject, Injectable } from '@angular/core';
import { AppSettings, StorageUtilityLib, USER_SESSION_TOKEN, UserSession } from '@thehubz/client/core';

@Injectable({
  providedIn: 'root',
})
export class AssetBasketService {
  private basketKey: string;
  private readonly assetBasketPrefix = AppSettings.BASKET;
  private readonly collectionBasketPrefix = AppSettings.COLLECTION_BASKET;
  private readonly resolvedUserId?: string;

  constructor(
    @Inject(USER_SESSION_TOKEN) userSessionToken: string,
    private storageUtility: StorageUtilityLib,
  ) {
    const user = storageUtility.getLocalStorage<UserSession>(userSessionToken);
    if (user && user.userData) {
      this.resolvedUserId = user.userData.impersonate?._id || user.userData._id;
      this.basketKey = `${this.assetBasketPrefix}_${this.resolvedUserId}`;
    } else {
      // could happen e.g. in sharing-client when there is no user session
      this.basketKey = 'no-user';
    }
  }

  /**
   * Switches the service to use the collection basket key.
   * Falls back to 'no-user' when no user id is available.
   */
  useCollectionBasket(collectionId: string) {
    const id = this.resolvedUserId;
    if (id) {
      this.basketKey = `${this.collectionBasketPrefix}_${id}_${collectionId}`;
    } else {
      this.basketKey = 'no-user';
    }
  }

  /**
   * Switches the service back to the asset basket key (original behavior).
   * Falls back to 'no-user' when no user id is available.
   */
  useAssetBasket() {
    const id = this.resolvedUserId;
    if (id) {
      this.basketKey = `${this.assetBasketPrefix}_${id}`;
    } else {
      this.basketKey = 'no-user';
    }
  }

  get assetIdsFromMyBasket(): string[] {
    const assets = this.storageUtility.getSessionStorageNullable<string[]>(this.basketKey);
    if (!assets) {
      return [];
    }
    return assets;
  }

  addToBasket(assetId: string) {
    const assets = this.assetIdsFromMyBasket;
    assets.push(assetId);
    this.storageUtility.setSessionStorage(this.basketKey, assets);
  }

  removeFromBasket(assetId: string) {
    const assets = this.assetIdsFromMyBasket;
    const idx = assets.indexOf(assetId);
    if (idx === -1) {
      return; // nothing to remove
    }
    assets.splice(idx, 1);
    if (assets.length === 0) {
      this.clearMyBasket();
      return;
    }
    this.storageUtility.setSessionStorage(this.basketKey, assets);
  }

  isInBasket(assetId: string): boolean {
    const assets = this.assetIdsFromMyBasket;
    return assets.includes(assetId);
  }

  clearMyBasket() {
    this.storageUtility.clearSessionStorage(this.basketKey);
  }
}
